import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ApiError, api, unwrap, type Schemas } from './client';

export type ImportJob = Schemas['ImportJobDto'];
export type CsvPreview = Schemas['CsvPreview'];
export type CsvOptions = Schemas['CsvOptions'];
export type ClockifyOptions = Schemas['ClockifyOptions'];

const jobsKey = (ws: string) => ['workspace', ws, 'imports'] as const;

export function useImportJobs(ws: string) {
  return useQuery({
    queryKey: jobsKey(ws),
    queryFn: () => unwrap(api.GET('/api/v1/workspaces/{workspaceId}/imports', { params: { path: { workspaceId: ws } } })),
    // Solange ein Job läuft, regelmäßig nachladen
    refetchInterval: (query) =>
      query.state.data?.some((j) => j.status === 'QUEUED' || j.status === 'RUNNING') ? 2000 : false,
  });
}

function readCookie(name: string) {
  return document.cookie
    .split('; ')
    .find((c) => c.startsWith(`${name}=`))
    ?.split('=')[1];
}

/** Multipart-Upload (openapi-fetch serialisiert FormData nicht typisiert). */
async function upload<T>(path: string, form: FormData): Promise<T> {
  const token = readCookie('XSRF-TOKEN');
  const response = await fetch(path, {
    method: 'POST',
    credentials: 'same-origin',
    headers: token ? { 'X-XSRF-TOKEN': decodeURIComponent(token) } : {},
    body: form,
  });
  const body = await response.json().catch(() => undefined);
  if (!response.ok) throw new ApiError(response.status, body);
  return body as T;
}

export function useCsvPreview(ws: string) {
  return useMutation({
    mutationFn: (file: File) => {
      const form = new FormData();
      form.append('file', file);
      return upload<CsvPreview>(`/api/v1/workspaces/${ws}/imports/csv/preview`, form);
    },
  });
}

export function useRunCsvImport(ws: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ file, options }: { file: File; options: CsvOptions }) => {
      const form = new FormData();
      form.append('file', file);
      form.append('options', new Blob([JSON.stringify(options)], { type: 'application/json' }));
      return upload<ImportJob>(`/api/v1/workspaces/${ws}/imports/csv`, form);
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['workspace', ws] }),
  });
}

export function useClockifyWorkspaces(ws: string) {
  return useMutation({
    mutationFn: (apiKey: string) =>
      unwrap(
        api.POST('/api/v1/workspaces/{workspaceId}/imports/clockify/workspaces', {
          params: { path: { workspaceId: ws } },
          body: { apiKey },
        }),
      ),
  });
}

export function useStartClockifyImport(ws: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: Schemas['ClockifyImportRequest']) =>
      unwrap(api.POST('/api/v1/workspaces/{workspaceId}/imports/clockify', { params: { path: { workspaceId: ws } }, body })),
    onSuccess: () => qc.invalidateQueries({ queryKey: jobsKey(ws) }),
  });
}
